import React from "react";
import {
  Box,
  Circle,
  Heading,
  Img,
  SimpleGrid,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { Link as GatsbyLink, withPrefix } from "gatsby";
import {
  HOME_PAGE_CONTAINER_PROPS,
  HomePageSection,
  HomePageSectionHeading,
} from "./HomePage";
import { upperFirst } from "lodash";
import { useButtonColor } from "@utils";

type TutorialTypeProps = {
  description: string;
  type: string;
  image: string;
};

function TutorialType({ description, type, image }: TutorialTypeProps) {
  const title = `${type}s`;
  const circleBg = useColorModeValue("blue.400", "whiteAlpha.100");
  const linkColor = useButtonColor("blue");
  return (
    <VStack
      role="group"
      as={GatsbyLink}
      to={`/browse?types=${type}`}
      textAlign="center"
    >
      <Circle mb="8" size={200} bg={circleBg}>
        <Img src={image} maxW={130} />
      </Circle>
      <Heading mb="2" as="h3" size="lg">
        {upperFirst(title)}
      </Heading>
      <Text mb="4" fontSize="lg">
        {description}
      </Text>
      <Text
        color={linkColor}
        fontSize="lg"
        fontWeight="semibold"
        _groupHover={{
          textDecor: "underline",
        }}
      >
        Explore {title}
      </Text>
    </VStack>
  );
}

export function HomePageTutorialTypes() {
  return (
    <Box
      bg="silver.300"
      _dark={{
        bg: "black.200",
      }}
      sx={HOME_PAGE_CONTAINER_PROPS}
    >
      <HomePageSection>
        <HomePageSectionHeading
          title="GraphQL tutorials and resources"
          description="Explore our materials, at your own pace."
          mb={{
            base: 6,
            md: 8,
            lg: 12,
            xl: 14,
          }}
          textAlign={{ md: "center" }}
        />
        <SimpleGrid
          mt={{
            base: 14,
            lg: 20,
          }}
          columns={{ md: 2, lg: 3 }}
          spacing={{
            base: 12,
            lg: 16,
            xl: 120,
          }}
        >
          <TutorialType
            type="course"
            description="Take any of our courses, individually and at your own pace."
            image={withPrefix("/assets/course.svg")}
          />
          <TutorialType
            type="lab"
            description="Get even more hands-on experience by building stuff in our labs."
            image={withPrefix("/assets/lab.svg")}
          />
          <TutorialType
            type="side quest"
            description="Learn from practical examples focused on real-world applications"
            image={withPrefix("/assets/side-quest.svg")}
          />
        </SimpleGrid>
      </HomePageSection>
    </Box>
  );
}
